import { CHUNK_NONCE_PREFIX_LENGTH, encryptFileToChunks, generateDek, toBase64, wrapKey } from '../crypto/backupCrypto';
import {
  abortMultipartUpload,
  completeMultipartUpload,
  initiateMultipartUpload,
  presignPart,
  presignUpload,
  UploadCryptoParams,
} from '../api/backupApi';

// Matches celepus-api's multipart PART_SIZE_HINT (app/controllers/api/v1/uploads_controller.rb)
// — keeping these equal means 1 encrypted chunk = 1 multipart part exactly.
const CHUNK_SIZE = 8 * 1024 * 1024;

// Below this, upload the whole (encrypted) file in one PUT rather than
// paying for a multipart initiate/complete round trip.
const MULTIPART_THRESHOLD = 20 * 1024 * 1024;

const putBytes = async (url: string, method: string, headers: Record<string, string>, body: BodyInit): Promise<Response> => {
  const response = await fetch(url, { method, headers, body });
  if (!response.ok) {
    throw new Error(`upload failed: HTTP ${response.status}`);
  }
  return response;
};

// Encrypts a file client-side (see crypto/backupCrypto.ts for the chunking
// scheme) and uploads it — as one simple presigned PUT for small files, or
// via the full S3 multipart flow for larger ones, streaming each encrypted
// chunk as its own part rather than buffering the whole file in memory.
const uploadFile = async (file: File, path: string, deviceUuid: string, masterKey: CryptoKey): Promise<void> => {
  const dek = await generateDek();
  const chunkNoncePrefix = crypto.getRandomValues(new Uint8Array(CHUNK_NONCE_PREFIX_LENGTH));
  const wrappedDek = await wrapKey(dek, masterKey);

  const cryptoParams: UploadCryptoParams = {
    wrapped_dek: wrappedDek.wrapped,
    dek_nonce: wrappedDek.nonce,
    chunk_nonce_prefix: toBase64(chunkNoncePrefix),
    chunk_size: CHUNK_SIZE,
    original_content_type: file.type || 'application/octet-stream',
    local_modified_at: new Date(file.lastModified).toISOString(),
  };

  if (file.size <= MULTIPART_THRESHOLD) {
    const encryptedChunks: Uint8Array[] = [];
    for await (const chunk of encryptFileToChunks(file, dek, CHUNK_SIZE, chunkNoncePrefix)) {
      encryptedChunks.push(chunk);
    }
    const totalLength = encryptedChunks.reduce((sum, c) => sum + c.length, 0);
    const body = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of encryptedChunks) {
      body.set(chunk, offset);
      offset += chunk.length;
    }

    const upload = await presignUpload(deviceUuid, path, cryptoParams);
    await putBytes(upload.url, upload.method, upload.headers, body);
    return;
  }

  const init = await initiateMultipartUpload(deviceUuid, path, cryptoParams);
  const completedParts: { part_number: number; etag: string }[] = [];
  let partNumber = 1;

  try {
    for await (const chunk of encryptFileToChunks(file, dek, CHUNK_SIZE, chunkNoncePrefix)) {
      const part = await presignPart(deviceUuid, init.uuid, partNumber);
      const response = await putBytes(part.url, part.method, {}, chunk);
      const etag = response.headers.get('etag');
      if (!etag) {
        throw new Error('missing ETag on multipart part response');
      }
      completedParts.push({ part_number: partNumber, etag });
      partNumber += 1;
    }
    await completeMultipartUpload(deviceUuid, init.uuid, completedParts);
  } catch (e) {
    await abortMultipartUpload(deviceUuid, init.uuid).catch(() => {});
    throw e;
  }
};

export {
  uploadFile,
};
